import { useEffect, useMemo, useRef } from "react";
import { collections, faqs, featureArticles, trendingList } from "@/mocks/homeContent";

interface Hit {
  anchor: string;
  section: string;
  icon: string;
  title: string;
  snippet: string;
}

export default function SearchModal({
  open,
  query,
  setQuery,
  onClose,
}: {
  open: boolean;
  query: string;
  setQuery: (q: string) => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const index: Hit[] = useMemo(() => {
    const list: Hit[] = [];
    featureArticles.forEach((a) =>
      list.push({ anchor: "featured", section: "深扒精选", icon: "ri-article-fill", title: a.title, snippet: a.summary }),
    );
    trendingList.forEach((t) =>
      list.push({ anchor: "hot", section: "今日热榜", icon: "ri-fire-fill", title: t.title, snippet: t.note }),
    );
    collections.forEach((c) =>
      list.push({ anchor: "collections", section: "专题合集", icon: "ri-folder-fill", title: c.title + " · " + c.subtitle, snippet: c.desc }),
    );
    faqs.forEach((f) => list.push({ anchor: "faq", section: "常见问题", icon: "ri-question-line", title: f.q, snippet: f.a }));
    return list;
  }, []);

  const q = query.trim().toLowerCase();
  const results = q ? index.filter((i) => (i.title + i.snippet).toLowerCase().includes(q)) : index.slice(0, 8);

  const go = (anchor: string) => {
    onClose();
    setQuery("");
    setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-foreground-950/60 p-4 pt-[10vh] backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl overflow-hidden rounded-xl bg-background-50 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-background-200 px-5 py-4">
          <i className="ri-search-line text-xl text-primary-500"></i>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索热点标题、栏目、专题…"
            className="w-full bg-transparent text-base text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          {query && (
            <button onClick={() => setQuery("")} aria-label="清空" className="flex h-7 w-7 items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-foreground-800">
              <i className="ri-close-circle-fill"></i>
            </button>
          )}
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto theme-scroll">
          {q && (
            <p className="px-5 pt-4 text-xs text-foreground-500">
              找到 {results.length} 条与「{query.trim()}」相关的结果，点击可跳转对应版块
            </p>
          )}
          {results.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-5 py-14 text-center">
              <i className="ri-file-search-line text-4xl text-foreground-300"></i>
              <p className="text-sm text-foreground-600">没有找到相关内容，换个关键词试试？</p>
              <button onClick={() => go("faq")} className="text-sm font-medium text-primary-600 hover:underline">
                也可以在常见问题中查看更多
              </button>
            </div>
          ) : (
            <ul className="p-3">
              {results.map((r) => (
                <li key={r.section + r.title}>
                  <button
                    onClick={() => go(r.anchor)}
                    className="group flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-background-100"
                  >
                    <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={r.icon}></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700">{r.title}</span>
                        <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800">
                          {r.section}
                        </span>
                      </span>
                      <span className="mt-1 block truncate text-xs text-foreground-500">{r.snippet}</span>
                    </span>
                    <i className="ri-corner-down-left-line mt-1 flex-none text-foreground-300"></i>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}